import {
   trackCustomGAEvent,
   trackGA4AddToCart,
   trackGA4RemoveFromCart,
   trackGA4ViewItem,
   trackGA4GrowthbookEvent,
   trackGA4PreferredStore,
   trackGA4PreferredLanguage,
   trackGA4PreferredLocale,
   trackGA4UserPrivilege,
   trackGA4VisitedStore,
   trackGA4PageBreadcrumbs,
   trackGA4FeaturedProducts,
   trackGA4CartUpdate,
} from './google';
import { trackClarityEvent, trackClarityTag } from './clarity';
import { trackTripleWhaleAddToCart } from './triple-whale';

export * from './google';

export type AnalyticsItem = {
   item_id: string | null | undefined;
   item_name: string | null;
   item_variant: string | null;
   numericProductId: string | null;
   quantity: number | null | undefined;
   price: number;
   categories?: string[];
   fulfiller?: string | null;
   oemPartnership?: string;
};

export type AnalyticsItemsEvent = {
   items: AnalyticsItem[];
   value: number;
   currency: string;
   localeCode?: string;
   shopifyCartId: string | undefined;
};

export type CustomAnalyticsEvent = {
   /**
    * Describes the type of events you want to track.
    * For example, Link Clicks, Videos, Outbound Links, and Form Events.
    */
   eventCategory: string;
   /**
    * The specific action that is taken.
    * For example, with a Video category, you might have a Play, Pause and Complete action.
    */
   eventAction: string;
   /**
    * Usually the title of the element that is being interacted with, to aid with analysis.
    * For example, it could be the name of a Video that was played or the specific
    * form that is being submitted.
    */
   eventName?: string;
   /**
    * A numeric value and is often added dynamically. It could be the cost of a
    * product that is added to a cart, or the completion percentage of a video.
    */
   eventValue?: number;
};

export type GrowthbookEvent = {
   experimentId: string;
   variationId: string;
};

export function trackInAnalyticsViewItem(event: AnalyticsItemsEvent) {
   trackGA4ViewItem(event);
   trackClarityEvent('productDetailView');
}

export function trackInAnalyticsAddToCart(event: AnalyticsItemsEvent) {
   trackGA4AddToCart(event);
   trackClarityEvent('addToCart');
   trackTripleWhaleAddToCart(event);
}

export function trackInAnalyticsRemoveFromCart(event: AnalyticsItemsEvent) {
   trackGA4RemoveFromCart(event);
   trackClarityEvent('removeFromCart');
}

export function getCategoriesFromAPICategories(apiCategories: {
   is_tool: boolean;
   item_category: string;
   part_subcategory: string;
}): string[] {
   const firstCategory = apiCategories.is_tool ? 'Tool' : 'Part';
   return [firstCategory, apiCategories.item_category, apiCategories.part_subcategory];
}

export function trackAnalyticsCustomEvent(props: CustomAnalyticsEvent) {
   trackCustomGAEvent(props);
   trackClarityEvent(props.eventCategory);
   const clarityAction = props.eventAction.replace(`${props.eventCategory} - `, '');
   trackClarityTag(props.eventCategory, clarityAction);
}

export function trackAnalyticsGrowthbookEvent({ variationId, experimentId }: GrowthbookEvent) {
   trackGA4GrowthbookEvent({
      eventName: 'experiment_viewed',
      experimentId,
      variationId,
   });
}

export function trackAnalyticsPreferredStore(storeDomain: string) {
   trackGA4PreferredStore(storeDomain);
}

export function trackAnalyticsPreferredLanguage(languageCode: string) {
   trackGA4PreferredLanguage(languageCode);
}

export function trackAnalyticsPreferredLocale(localeCode: string) {
   trackGA4PreferredLocale(localeCode);
}

export function trackAnalyticsUserPrivilege(userPrivilege: string | null) {
   trackGA4UserPrivilege(userPrivilege);
}

export function trackAnalyticsVisitedStore() {
   trackGA4VisitedStore();
}

export type Breadcrumb = {
   label: string;
   url?: string;
};

export function formatBreadcrumbsForAnalytics(breadcrumbs: Breadcrumb[]): string | null {
   if (typeof window === 'undefined' || breadcrumbs.length < 2) {
      return null;
   }
   if (
      !breadcrumbs[breadcrumbs.length - 1]?.url ||
      breadcrumbs[breadcrumbs.length - 1]?.url === '#'
   ) {
      // clone the array because we're replacing the last element
      breadcrumbs = breadcrumbs.slice();
      const last = breadcrumbs[breadcrumbs.length - 1];
      // clone this element cause we're mutating it
      breadcrumbs[breadcrumbs.length - 1] = { ...last, url: window.location.pathname };
   }
   return (
      ':' +
      breadcrumbs
         .map(breadcrumb => decodeURIComponent(breadcrumb.url?.split('/').pop() ?? ''))
         .join(':') +
      ':'
   );
}

export function trackAnalyticsPageBreadcrumbs(breadcrumbs: Breadcrumb[]) {
   const formattedBreadcrumbs = formatBreadcrumbsForAnalytics(breadcrumbs);
   if (formattedBreadcrumbs) {
      trackGA4PageBreadcrumbs(formattedBreadcrumbs);
   }
}

export function trackAnalyticsFeaturedProducts(productSKUs: string[]) {
   trackGA4FeaturedProducts(productSKUs);
}

export function trackAnalyticsCartUpdate(event: AnalyticsItemsEvent) {
   trackGA4CartUpdate(event);
}

const currencyConversions = {
   AUD: 0.75,
   CAD: 0.815,
   CHF: 1.23,
   EUR: 1.15,
   GBP: 1.3,
   NOK: 0.097,
   NZD: 0.59,
   USD: 1,
};

export type CurrencyCode = keyof typeof currencyConversions;

export function toUSD(price: number, currencyCode: CurrencyCode) {
   const currencyConversionRatio = currencyConversions[currencyCode] ?? 1;
   return price * currencyConversionRatio;
}
